import { Button } from '@/components/ui/Button';
import {
  Card,
  CardContent,
  CardFooter,
  CardHeader,
  CardTitle,
} from '@/components/ui/Card';
import { Input } from '@/components/ui/Input';
import {
  AuditLogInsertInput,
  Customer,
  CustomerPkColumnsInput,
  CustomerSetInput,
} from '@/generated/graphql';
import { execute } from '@/graphql/execute';
import { AuditLogAction, AuditLogEntityType } from '@/models/audit-log.models';
import { CustomerConfig } from '@/models/customer.models';
import { createAuditLogInput } from '@/utils/audit-log.function';
import { useMutation } from '@tanstack/react-query';
import { Copy, Pencil } from 'lucide-react';
import React, { useEffect, useMemo, useState } from 'react';
import { useForm } from 'react-hook-form';
import { toast } from 'sonner';
import { v4 as uuid } from 'uuid';
import {
  applyCustomerSecretConfigValues,
  buildCustomerSecretOperations,
  createDefaultCustomerSecretDrafts,
  createSecretUpsertDraft,
  CustomerSecretDrafts,
  CustomerSecretTarget,
  duplicateVlmProfileIds,
  getConfigValueAtPath,
  getCustomerSecretDraft,
  manageableCustomerSecretTargets,
  stagedCustomerSecretDeletes,
  vlmGatewayProfileTargets,
  vlmProfileApiKeyReferences,
  VlmProfileRemoval,
  vlmProfileRemovals,
  VlmProfileRename,
  vlmProfileRenames,
  withVlmProfileIds,
} from '../../customerSecretStore';
import { updateCustomerMutation } from '../../graphql/mutation';
import { mergeFormConfig } from '../../mergeCustomerConfig';
import {
  CustomerSecretSaveError,
  CustomerSecretSaveResult,
  deleteCustomerSecrets,
  saveCustomerSecretOperations,
} from '../../saveCustomerSecrets';
import {
  applyVlmProfileSettings,
  editableVlmProfileNames,
  invalidVlmProfileNames,
  readAllVlmProfileSettings,
  removeVlmProfiles,
  renameVlmProfiles,
} from '../../vlmGatewayProfileSettings';
import CustomerDetailsForm, {
  CustomerFormMode,
  CustomerFormValues,
  LEGACY_SENPIC_CONFIG_KEYS,
  normalizeCustomerConfig,
} from '../CustomerDetailsForm';
import { VlmSettingsReveal } from '../VlmGatewayCard';

type CustomerConfigTabProps = {
  customer: Customer;
  refetch: () => Promise<unknown>;
  refetchLog: () => Promise<unknown>;
};

export const CustomerConfigTab: React.FC<CustomerConfigTabProps> = ({
  customer,
  refetch,
  refetchLog,
}) => {
  const [editing, setEditing] = useState(false);
  const [formMode, setFormMode] = useState<CustomerFormMode>('form');
  const [secretDrafts, setSecretDrafts] = useState<CustomerSecretDrafts>(
    createDefaultCustomerSecretDrafts(),
  );
  const [vlmSettingsReveal, setVlmSettingsReveal] =
    useState<VlmSettingsReveal>();
  // Ids for profiles stored without one, made once per load: staged keys are
  // keyed by them, and the save stores them.
  const storedConfig = useMemo(
    () => withVlmProfileIds(customer.config, { storedNames: true }),
    [customer.config],
  );
  const initialVlmProfiles = useMemo(
    () => readAllVlmProfileSettings(storedConfig),
    [storedConfig],
  );

  const {
    register,
    reset,
    getValues,
    setError,
    control,
    formState: { isValid },
  } = useForm<CustomerFormValues>({});

  useEffect(() => {
    if (customer) {
      reset({
        name: customer.name,
        storagePath: customer.storagePath,
        config: normalizeCustomerConfig(storedConfig),
        jsonConfig: storedConfig,
        vlmProfiles: initialVlmProfiles,
        vlmAddedProfileIds: {},
        vlmRemovedProfiles: [],
        vlmRenamedProfiles: {},
      });
      setSecretDrafts(createDefaultCustomerSecretDrafts());
    }
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [customer]);

  const { mutateAsync: updateCustomer } = useMutation({
    mutationKey: ['UpdateCustomer'],
    mutationFn: ({
      setInput,
      id,
      auditLogInput,
    }: {
      setInput: CustomerSetInput;
      id: string;
      auditLogInput: AuditLogInsertInput;
    }) => {
      const pkColumnInput: CustomerPkColumnsInput = { id };
      return execute({
        query: updateCustomerMutation,
        variables: { pkColumnInput, setInput, auditLogInput },
      });
    },
  });

  const handleFormSubmit = async () => {
    const customerId = customer.id;
    const formValues = getValues();
    const values = { ...formValues };
    delete values.jsonConfig;
    delete values.vlmProfiles;
    delete values.vlmAddedProfileIds;
    delete values.vlmRemovedProfiles;
    delete values.vlmRenamedProfiles;

    const serializeSystemPrompts = (config: CustomerConfig) => {
      const serializeLlm = <T extends { SystemPrompt: string }>(llm: T): T => ({
        ...llm,
        SystemPrompt: llm.SystemPrompt
          ? JSON.stringify(llm.SystemPrompt)
          : undefined,
      });
      const ar = config.AlertReviewer;
      if (!ar) return config;
      return {
        ...config,
        AlertReviewer: {
          ...ar,
          Gpt: serializeLlm(ar.Gpt),
          Claude: serializeLlm(ar.Claude),
          Gemini: serializeLlm(ar.Gemini),
        },
      };
    };

    let baseConfig: Record<string, unknown>;
    let removals: VlmProfileRemoval[] = [];
    let renames: VlmProfileRename[] = [];
    if (formMode === 'form') {
      const merged = mergeFormConfig(storedConfig, {
        ...serializeSystemPrompts({ ...formValues.config }),
        ...Object.fromEntries(
          LEGACY_SENPIC_CONFIG_KEYS.map((key) => [key, undefined]),
        ),
      });
      const listedProfiles = formValues.vlmProfiles ?? {};
      const addedNames = Object.keys(listedProfiles).filter(
        (name) => !Object.hasOwn(initialVlmProfiles, name),
      );
      removals = vlmProfileRemovals(
        storedConfig,
        formValues.vlmRemovedProfiles ?? [],
      );
      const removedNames = new Set(removals.map((removal) => removal.name));
      // The same check locks the settings inputs, so it governs the write too.
      const editableNames = editableVlmProfileNames(storedConfig);
      const vlmProfiles = Object.fromEntries(
        Object.entries(listedProfiles).filter(
          ([name]) =>
            !removedNames.has(name) &&
            (editableNames.has(name) || addedNames.includes(name)),
        ),
      );
      const invalidNames = invalidVlmProfileNames(vlmProfiles);
      if (invalidNames.length > 0) {
        setVlmSettingsReveal({ profileNames: invalidNames });
        toast.error(
          `Fix the VLM gateway profile settings for ${invalidNames.join(', ')} before saving.`,
        );
        return;
      }
      baseConfig = applyVlmProfileSettings(
        merged,
        initialVlmProfiles,
        vlmProfiles,
        formValues.vlmAddedProfileIds ?? {},
      );
      // Planned against the config being saved, so added profiles count as taken names.
      renames = vlmProfileRenames(
        baseConfig,
        Object.fromEntries(
          Object.entries(formValues.vlmRenamedProfiles ?? {}).filter(
            ([from]) => !removedNames.has(from) && editableNames.has(from),
          ),
        ),
      );
    } else {
      // Profiles added in JSON mode get an id here.
      baseConfig = withVlmProfileIds({ ...formValues.jsonConfig }) as Record<
        string,
        unknown
      >;
    }

    const duplicateIds = duplicateVlmProfileIds(baseConfig);
    if (duplicateIds.length > 0) {
      toast.error(
        `Not saved: several VLM gateway profiles have the Id ${duplicateIds.join(', ')}, so they would share one API key. If one is a copy, remove its Id and ApiKey, then set its key in Form mode.`,
      );
      return;
    }

    // Renaming keeps a profile's Id, so its key and staged key change go with it.
    const configToSave = renameVlmProfiles(
      removeVlmProfiles(
        baseConfig,
        removals.map((removal) => removal.name),
      ),
      renames,
    );
    const secretTargets = manageableCustomerSecretTargets(configToSave);
    const savingTargetIds = new Set(secretTargets.map((target) => target.id));
    // A removed profile's key goes with it, so its staged change is dropped.
    const removedTargetIds = new Set(
      vlmGatewayProfileTargets(storedConfig)
        .filter((target) =>
          removals.some((removal) => removal.name === target.profileName),
        )
        .map((target) => target.id),
    );
    const knownTargets = new Map(
      [
        ...manageableCustomerSecretTargets(storedConfig),
        ...manageableCustomerSecretTargets(baseConfig),
      ].map((target) => [target.id, target]),
    );
    const droppedLabels = [...knownTargets.values()]
      .filter(
        (target) =>
          !savingTargetIds.has(target.id) &&
          !removedTargetIds.has(target.id) &&
          getCustomerSecretDraft(secretDrafts, target.id).action !== 'none',
      )
      .map((target) => target.label);
    if (droppedLabels.length > 0) {
      toast.error(
        `Not saved: ${droppedLabels.join(', ')}. Fix the VlmGateway section in JSON mode first.`,
      );
      return;
    }

    // Keys are written before the config save, and old keys are deleted after
    // it, so config never points at a deleted key.
    let saved: CustomerSecretSaveResult;
    let secretError: CustomerSecretSaveError | undefined;

    try {
      saved = await saveCustomerSecretOperations(
        customerId,
        buildCustomerSecretOperations(secretDrafts, secretTargets),
      );
    } catch (error) {
      if (!(error instanceof CustomerSecretSaveError)) {
        toast.error('Failed to update secrets.', {
          duration: 4000,
          description: error instanceof Error ? error.message : undefined,
        });
        return;
      }
      // Still save what senhub completed, so config matches the vault. The
      // failed and unattempted targets keep their stored reference.
      saved = error;
      secretError = error;
    }

    const stagedDeletes = stagedCustomerSecretDeletes(
      secretDrafts,
      secretTargets,
    );
    const configWithSecretValues = applyCustomerSecretConfigValues(
      configToSave,
      {
        ...saved.configValues,
        ...Object.fromEntries(stagedDeletes.map((target) => [target.id, ''])),
      },
      secretTargets,
    );

    try {
      await updateCustomer({
        id: customerId,
        setInput: {
          ...values,
          config: configWithSecretValues,
          rowVersion: uuid(),
        },
        auditLogInput: createAuditLogInput({
          action: AuditLogAction.UPDATE_CUSTOMER,
          entities: [{ id: customerId, entity: AuditLogEntityType.CUSTOMER }],
        }),
      });
    } catch (error) {
      // Only the config write is in this try, so nothing below has run.
      toast.error('Failed to save the customer. No API keys were deleted.', {
        duration: 4000,
        description: error instanceof Error ? error.message : undefined,
      });
      return;
    }

    // A key another profile still points at stays, e.g. a copy's in JSON mode.
    const storedTargets = vlmGatewayProfileTargets(storedConfig);
    const storedReference = (target: CustomerSecretTarget) =>
      getConfigValueAtPath(
        storedConfig,
        (storedTargets.find((stored) => stored.id === target.id) ?? target)
          .configPath,
      ).trim();
    const stillReferenced = vlmProfileApiKeyReferences(configWithSecretValues);
    const deleteCandidates = [
      ...stagedDeletes,
      ...removals.flatMap((removal) =>
        removal.keyTarget ? [removal.keyTarget] : [],
      ),
    ];
    const sharedKeys = deleteCandidates.filter((target) =>
      stillReferenced.has(storedReference(target)),
    );
    const deleted = await deleteCustomerSecrets(
      customerId,
      deleteCandidates.filter((target) => !sharedKeys.includes(target)),
    );

    if (secretError) {
      toast.error(
        `Failed to update ${secretError.target.label}. Other changes were saved.`,
        { duration: 4000, description: secretError.message },
      );
    } else {
      toast.success('Customer updated successfully!', { duration: 3000 });
    }

    if (deleted.failed.length > 0) {
      toast.warning(
        `Saved, but these API keys could not be deleted and were left in the vault, unused: ${deleted.failed
          .map((target) => target.label)
          .join(', ')}.`,
      );
    }

    if (sharedKeys.length > 0) {
      toast.info(
        `Kept ${sharedKeys
          .map((target) => target.label)
          .join(', ')} in the vault: another profile still uses it.`,
      );
    }

    if (deleted.leftInPlace.length > 0) {
      toast.info(
        `Removed the reference for ${deleted.leftInPlace
          .map((target) => target.label)
          .join(
            ', ',
          )}. The secret wasn't created by the console and was left in place.`,
      );
    }

    const auditFailures = [...saved.auditFailures, ...deleted.auditFailures];
    if (auditFailures.length > 0) {
      toast.warning(
        `Saved, but the audit log entry for ${auditFailures
          .map((target) => target.label)
          .join(', ')} could not be written.`,
      );
    }

    await refetch();
    await refetchLog();
    setSecretDrafts(createDefaultCustomerSecretDrafts());
    setEditing(false);
  };

  const handleToggleEditing = () => {
    setEditing(!editing);
    if (editing) {
      reset();
      setSecretDrafts(createDefaultCustomerSecretDrafts());
    }
  };

  return (
    <Card className="relative">
      <CardHeader>
        <CardTitle>Configuration</CardTitle>
        <Button
          className="absolute top-4 right-4"
          variant={'outline'}
          size={'sm'}
          onClick={handleToggleEditing}
        >
          {editing ? (
            'Cancel Editing'
          ) : (
            <>
              <Pencil />
              Edit
            </>
          )}
        </Button>
      </CardHeader>
      <CardContent className="grid gap-6">
        <div className="relative">
          <Input
            readOnly
            type="text"
            id="id"
            placeholder="Id"
            defaultValue={customer.id}
            className="truncate pr-8"
          />
          <Button
            type="button"
            className="text-muted-foreground hover:text-primary absolute top-0 right-0 hover:cursor-pointer"
            size={'icon'}
            variant={'link'}
            onClick={() => {
              toast.message('Copied!', { duration: 1000 });
              void navigator.clipboard.writeText(customer.id);
            }}
          >
            <Copy className="size-5" />
          </Button>
        </div>
        <CustomerDetailsForm
          register={register}
          control={control}
          setError={setError}
          readOnly={!editing}
          formMode={formMode}
          setFormMode={setFormMode}
          secretDrafts={secretDrafts}
          storedConfig={storedConfig}
          vlmSettingsReveal={vlmSettingsReveal}
          onStageSecretUpsert={(targetId, credentials) => {
            setSecretDrafts((t) => ({
              ...t,
              [targetId]: createSecretUpsertDraft(credentials),
            }));
          }}
          onStageSecretDelete={(targetId) => {
            setSecretDrafts((t) => ({
              ...t,
              [targetId]: { action: 'delete' },
            }));
          }}
          onDiscardSecretDraft={(targetId) => {
            setSecretDrafts((t) => {
              const { [targetId]: _discarded, ...rest } = t;
              return rest;
            });
          }}
        />
      </CardContent>
      {editing && (
        <CardFooter>
          <div className="mt-4 flex w-full justify-end gap-4">
            <Button
              variant={'outline'}
              className="w-30"
              size={'sm'}
              onClick={handleToggleEditing}
            >
              Cancel
            </Button>
            <Button
              disabled={!isValid}
              variant={'default'}
              className="w-30"
              onClick={() => void handleFormSubmit()}
              size={'sm'}
            >
              Save
            </Button>
          </div>
        </CardFooter>
      )}
    </Card>
  );
};
